// Browser SpeechSynthesis-based TTS provider.
//
// The provider speaks one segment at a time. The caller (ReaderView) drives
// the playlist: when one segment ends, it requests the next, which gives us
// clean pause/resume semantics and decouples the queue from the engine.
//
// Notes on the browser engine:
//   - Voice list is loaded asynchronously on Chromium; we expose a "ready"
//     promise that resolves once voices show up.
//   - Long utterances are unstable in Chrome — we keep segments paragraph-
//     sized which mitigates that.

import type { TTSOptions, TTSProvider, TextSegment } from "@/types/tts";
import { remoteUrl } from "./remoteConfig";

/** True when the current browser exposes the SpeechSynthesis API.
 *  NOTE: this describes the BROWSER engine only. Server TTS (Piper /
 *  Kokoro) needs nothing from the browser but <audio> — use
 *  canReadAloud() for "can this device read aloud at all". */
export function isTTSSupported(): boolean {
  return typeof window !== "undefined" && !!window.speechSynthesis;
}

/** Can this device read aloud with the given provider?
 *
 *  2026-09-29 — "Play pause button not loading". The Play tile was gated on
 *  isTTSSupported(), i.e. on window.speechSynthesis existing. But the app's
 *  default and primary engine is the SERVER one, which only needs an
 *  <audio> element. On any webview without SpeechSynthesis — some iOS
 *  standalone/PWA configurations, the BOOX browser, embedded webviews — the
 *  button therefore never rendered at all, even though server TTS was
 *  reachable and working. The button was ABSENT, not inert.
 *
 *  Only the explicitly-chosen "browser" provider actually requires the
 *  Web Speech API. */
export function canReadAloud(provider: string): boolean {
  if (typeof window === "undefined") return false;
  if (provider === "browser") return isTTSSupported();
  return typeof window.Audio === "function";
}

let voicesCache: SpeechSynthesisVoice[] | null = null;
let voicesPromise: Promise<SpeechSynthesisVoice[]> | null = null;

/** Synchronous accessor — returns [] until voices have been resolved once. */
export function getCachedVoices(): SpeechSynthesisVoice[] {
  return voicesCache ?? [];
}

export function listVoices(): Promise<SpeechSynthesisVoice[]> {
  if (!isTTSSupported()) return Promise.resolve([]);
  if (voicesPromise) return voicesPromise;
  voicesPromise = new Promise((resolve) => {
    const finish = (voices: SpeechSynthesisVoice[]) => {
      voicesCache = voices;
      resolve(voices);
    };
    const tryGet = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        finish(voices);
        return true;
      }
      return false;
    };
    if (tryGet()) return;
    window.speechSynthesis.onvoiceschanged = () => {
      tryGet();
    };
    // Safari sometimes never fires voiceschanged — poll briefly.
    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      if (tryGet() || tries > 30) {
        clearInterval(interval);
        if (tries > 30) finish([]);
      }
    }, 100);
  });
  return voicesPromise;
}

export class BrowserSpeechSynthesisProvider implements TTSProvider {
  name = "browser";

  speakSegment(segment: TextSegment, options: TTSOptions): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!window.speechSynthesis) {
        reject(new Error("SpeechSynthesis not supported in this browser."));
        return;
      }
      // Cancel anything still in the queue from a previous segment.
      window.speechSynthesis.cancel();

      const utter = new SpeechSynthesisUtterance(segment.text);
      utter.rate = clamp(options.rate, 0.5, 2.0);
      utter.pitch = clamp(options.pitch, 0.5, 2.0);
      utter.volume = clamp(options.volume, 0, 1);
      if (options.voice) utter.voice = options.voice;

      if (options.onBoundary) {
        utter.onboundary = (ev: SpeechSynthesisEvent) => {
          // Some engines fire boundary for both word and sentence.
          // We forward all of them; the caller decides what to do.
          options.onBoundary!(ev.charIndex, ev.charLength ?? 0);
        };
      }

      // Emit a synthetic "speech started" signal so the caller can wait
      // for actual audio onset before kicking off word-highlight ticks.
      // Browser voices that DO fire onboundary will overwrite this with
      // the real first-word position; voices that DON'T fire boundary
      // (most iOS voices) still get this single signal at start.
      utter.onstart = () => {
        try {
          options.onBoundary?.(0, 0);
        } catch {
          /* defensive — onBoundary is user code */
        }
      };
      utter.onend = () => resolve();
      utter.onerror = (ev) => {
        // "interrupted" / "canceled" fire on stop() — treat as graceful end.
        if (
          ev.error === "interrupted" ||
          ev.error === "canceled"
        ) {
          resolve();
          return;
        }
        reject(new Error(`TTS error: ${ev.error}`));
      };

      window.speechSynthesis.speak(utter);
    });
  }

  pause(): void {
    window.speechSynthesis?.pause();
  }
  resume(): void {
    window.speechSynthesis?.resume();
  }
  stop(): void {
    window.speechSynthesis?.cancel();
  }
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

// ─────────────────────────────────────────────────────────────────────────
// Server-side neural TTS provider — calls the Piper proxy at /tts.
//
// Audio comes back as a WAV blob. We play it through an HTMLAudioElement,
// translate audio.currentTime into char offsets within the segment text
// (linear assumption — good enough for word-by-word highlight tracking
// with paragraph-sized chunks), and pre-fetch the next segment in
// parallel for gapless playback.
// ─────────────────────────────────────────────────────────────────────────

const PIPER_TTS_ENDPOINT = "/tts/tts";
const KOKORO_TTS_ENDPOINT = "/tts-kokoro/tts";

/** Build the endpoint URL for a given server family. Relative in
 *  production (the deploy host proxies /tts); on a localhost dev
 *  server it prefixes VITE_REMOTE_BASE so audio still works. */
function endpointFor(kind: "piper" | "kokoro"): string {
  const path = kind === "kokoro" ? KOKORO_TTS_ENDPOINT : PIPER_TTS_ENDPOINT;
  return remoteUrl(path);
}

/** Legacy alias — still used by the default-construction path. */
function ttsEndpoint(): string {
  return endpointFor("piper");
}

interface ServerHealth {
  ok: boolean;
  voices: string[];
  default_voice?: string;
  version?: string;
}

let healthPromise: Promise<ServerHealth | null> | null = null;
export function checkServerTTS(): Promise<ServerHealth | null> {
  if (healthPromise) return healthPromise;
  const url = ttsEndpoint().replace(/\/tts$/, "/health");
  healthPromise = fetch(url, { method: "GET" })
    .then((r) => (r.ok ? (r.json() as Promise<ServerHealth>) : null))
    .catch(() => null)
    // 2026-09-27: only a SUCCESSFUL probe may be memoised. Previously the
    // cached promise held a null forever, so one blip — the tunnel cycling,
    // the phone's radio not up yet on a home-screen launch, a cold Kokoro —
    // silently disabled voice for the entire life of the page with no retry
    // anywhere except the Settings "recheck" button. Clearing the cache on
    // failure lets the next render (or the next Play tap) probe again.
    .then((h) => {
      if (!h) healthPromise = null;
      return h;
    });
  return healthPromise;
}
/** Force a fresh server health probe — used by the settings UI. */
export function recheckServerTTS(): Promise<ServerHealth | null> {
  healthPromise = null;
  return checkServerTTS();
}

export class ServerTTSProvider implements TTSProvider {
  name = "server";
  private audio: HTMLAudioElement | null = null;
  // Small keyed prefetch cache (insertion-ordered, capped). A single
  // "next" slot was clobbered every loop iteration: ReaderView prefetches
  // segment i+1 BEFORE speaking segment i, so the slot held i+1 when the
  // speak of i looked for i — a guaranteed miss. Every segment was fetched
  // twice and played cold (countdown flashes between paragraphs). (2026-07-10)
  private prefetchCache = new Map<string, Promise<Blob>>();
  private static PREFETCH_MAX = 3;
  private blobUrl: string | null = null;
  private endpoint: string;
  private defaultVoice: string;
  // Active speak resolver — lets stop() actively settle the pending
  // speakSegment promise. Without this, stop() pauses the audio but the
  // outer await in ReaderView's play loop hangs forever because no
  // 'ended' or 'error' event ever fires on a paused-and-discarded audio
  // element. That hang is what showed up as "audio doesn't stop": the
  // loop's await never resolved, so the next pause/play sequence could
  // start a parallel loop or trigger a stale handle to keep speaking.
  private resolveActive: (() => void) | null = null;

  // Single reusable <audio> element. iOS Safari grants play() permission per
  // element instance once it has played inside a user gesture; a fresh
  // `new Audio()` per segment loses that grant, so play() after the network
  // await is blocked and the reader goes silent ("stalls in Safari"). We keep
  // ONE element, unlock it on the Play tap, and just swap its src per segment.
  private el: HTMLAudioElement | null = null;
  // Hard-stop latch: once stop() sets this, no in-flight or queued segment may
  // play until the next Play tap clears it (unlock). Prevents the "audio got
  // stuck on" resurrection where the loop starts the next segment on the shared
  // element a beat after stop().
  private stopped = false;
  // A valid, silent 44-byte WAV. Playing it inside the tap activates the el.
  private static SILENT_WAV =
    "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=";

  private getEl(): HTMLAudioElement | null {
    if (!this.el && typeof Audio !== "undefined") {
      this.el = new Audio();
      this.el.preload = "auto";
    }
    return this.el;
  }

  /** Activate the shared audio element from within a user gesture (Play tap).
   *  Safe to call repeatedly; only the first play-in-gesture matters. */
  unlock(): void {
    this.stopped = false;
    const el = this.getEl();
    if (!el) return;
    try {
      el.src = ServerTTSProvider.SILENT_WAV;
      const p = el.play();
      if (p && typeof p.then === "function") {
        p.then(() => {
          try { el.pause(); el.currentTime = 0; } catch { /* ignore */ }
        }).catch(() => { /* gesture may still register the element */ });
      }
    } catch { /* best-effort */ }
  }

  constructor(opts?: { endpoint?: string; voice?: string }) {
    this.endpoint = opts?.endpoint ?? ttsEndpoint();
    // Lessac sounds noticeably more natural than amy for long-form
    // reading; both are local and equally fast.
    this.defaultVoice = opts?.voice ?? "en_US-lessac-medium";
  }

  private async fetchAudioOnce(text: string, voice: string, rate: number): Promise<Blob> {
    const r = await fetch(this.endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, voice, rate }),
    });
    if (!r.ok) throw new Error(`TTS server ${r.status}`);
    const raw = await r.blob();
    // iOS Safari refuses to decode a blob: URL whose MIME is empty/unknown.
    // Piper/Kokoro return WAV; stamp the type explicitly so <audio> can play.
    if (raw.type && /^audio\//i.test(raw.type)) return raw;
    const ct = r.headers.get("content-type") || "";
    return new Blob([raw], { type: /^audio\//i.test(ct) ? ct : "audio/wav" });
  }

  /** Fetch with a couple of quick retries.
   *
   *  2026-09-29: there was no retry anywhere in this path, and ReaderView's
   *  play loop `break`s on a thrown segment — so ONE blip (the tunnel
   *  cycling, a phone's radio dropping between paragraphs, a 502 from a
   *  busy Kokoro) permanently ended the reading session. Measured against
   *  the live endpoints: 20/20 paragraph requests succeeded, but Kokoro's
   *  synthesis time varied from 1.0s to 7.7s under load, which is exactly
   *  the regime where a timeout or a dropped connection is plausible on
   *  cellular.
   *
   *  Deliberately short and few: audio is time-critical. Two retries at
   *  400ms and 1200ms add at most ~1.6s before we give up, which is well
   *  inside the patience the 3-2-1 countdown already buys us. */
  private async fetchAudio(text: string, voice: string, rate: number): Promise<Blob> {
    const delays = [400, 1200];
    let lastErr: unknown;
    for (let attempt = 0; attempt <= delays.length; attempt++) {
      if (this.stopped) throw new Error("stopped");
      try {
        return await this.fetchAudioOnce(text, voice, rate);
      } catch (err) {
        lastErr = err;
        if (attempt < delays.length) {
          await new Promise((r) => setTimeout(r, delays[attempt]));
        }
      }
    }
    throw lastErr instanceof Error ? lastErr : new Error("TTS fetch failed");
  }

  /** Pre-fetch the audio for an upcoming segment so playback is gapless. */
  prefetch(segment: TextSegment, options: TTSOptions): void {
    if (this.prefetchCache.has(segment.id)) return;
    const voice = (options as TTSOptions & { serverVoice?: string }).serverVoice
      ?? this.defaultVoice;
    const promise = this.fetchAudio(segment.text, voice, options.rate).catch(() => {
      this.prefetchCache.delete(segment.id);
      throw new Error("prefetch failed");
    });
    // Suppress the unhandled-rejection page error when nothing ever awaits
    // this prefetch (e.g. user stops before the next segment). Awaiters via
    // speakSegment still see the original rejection. (2026-07-10)
    promise.catch(() => { /* handled: quiet prefetch failure */ });
    this.prefetchCache.set(segment.id, promise);
    // Evict oldest entries beyond the cap.
    while (this.prefetchCache.size > ServerTTSProvider.PREFETCH_MAX) {
      const oldest = this.prefetchCache.keys().next().value as string;
      this.prefetchCache.delete(oldest);
    }
  }

  async speakSegment(segment: TextSegment, options: TTSOptions): Promise<void> {
    // CRITICAL: tear down any previous audio first. Without this, a fresh
    // speakSegment overwrites this.audio while the previous Audio element
    // keeps playing — leading to the 'two voices at once' bug.
    if (this.audio) {
      try { this.audio.pause(); } catch { /* ignore */ }
      this.audio = null;
    }

    const voice =
      (options as TTSOptions & { serverVoice?: string }).serverVoice
      ?? this.defaultVoice;
    let blob: Blob;
    const cached = this.prefetchCache.get(segment.id);
    if (cached) {
      this.prefetchCache.delete(segment.id);
      try {
        blob = await cached;
      } catch {
        // Prefetch failed (network blip) — retry cold rather than dying.
        blob = await this.fetchAudio(segment.text, voice, options.rate);
      }
    } else {
      blob = await this.fetchAudio(segment.text, voice, options.rate);
    }
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
    // stop() may have fired while we were fetching — don't start audio.
    if (this.stopped) return;

    const url = URL.createObjectURL(blob);
    this.blobUrl = url;

    return new Promise<void>((resolve, reject) => {
      if (this.stopped) { resolve(); return; }
      // Reuse the single (unlocked) element rather than `new Audio()` — that
      // is what lets play() succeed on iOS Safari after the network await.
      const audio = this.getEl();
      if (!audio) { reject(new Error("Audio unavailable")); return; }
      try { audio.pause(); } catch { /* ignore */ }
      audio.volume = clamp(options.volume, 0, 1);
      // A fresh src always begins at 0; setting currentTime before the new
      // media loads makes iOS Safari re-seek/restart mid-swap, which showed
      // up as the audio restarting between paragraphs. Just swap the src.
      audio.src = url;
      this.audio = audio;
      // Record the resolver so stop() can short-circuit the await even
      // when no DOM event would naturally fire (audio paused, src
      // cleared, etc).
      this.resolveActive = () => {
        this.resolveActive = null;
        resolve();
      };

      let lastReportedChar = -1;
      const onTime = () => {
        const dur = audio.duration;
        if (!dur || !isFinite(dur)) return;
        const progress = audio.currentTime / dur;
        const charIdx = Math.floor(progress * segment.text.length);
        if (charIdx !== lastReportedChar) {
          lastReportedChar = charIdx;
          options.onBoundary?.(charIdx, 0);
        }
      };
      const onEnded = () => { cleanup(); resolve(); };
      const onError = () => { cleanup(); reject(new Error("Audio playback failed")); };
      // Remove THIS segment's listeners on settle so they don't stack on the
      // reused element. Pause but keep the element (and its iOS grant) alive.
      const cleanup = () => {
        audio.removeEventListener("timeupdate", onTime);
        audio.removeEventListener("ended", onEnded);
        audio.removeEventListener("error", onError);
        try { audio.pause(); } catch { /* ignore */ }
        if (this.audio === audio) this.audio = null;
        if (this.resolveActive) this.resolveActive = null;
      };

      audio.addEventListener("timeupdate", onTime);
      audio.addEventListener("ended", onEnded);
      audio.addEventListener("error", onError);

      const play = audio.play();
      if (play && typeof play.catch === "function") {
        play.catch((err) => {
          cleanup();
          reject(err);
        });
      }
    });
  }

  pause(): void {
    this.audio?.pause();
  }
  resume(): void {
    void this.audio?.play();
  }
  stop(): void {
    this.stopped = true;
    // Kill the shared element directly too (this.audio may already be null
    // between segments while the element itself is still mid-play).
    if (this.el) {
      try { this.el.pause(); } catch { /* ignore */ }
      try { this.el.src = ""; } catch { /* ignore */ }
    }
    if (this.audio) {
      try { this.audio.pause(); } catch { /* ignore */ }
      try { this.audio.currentTime = 0; } catch { /* ignore */ }
      // Clearing src forces the browser to release the media buffer and
      // prevents resurrection on subsequent play() — without this, iOS
      // sometimes lets a "stopped" element resume from where it was.
      try { this.audio.src = ""; } catch { /* ignore */ }
    }
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
    this.audio = null;
    // Forcibly settle the outstanding speakSegment promise so the play
    // loop's await releases and re-checks playingRef on the next tick.
    // Without this, the await hangs and the next play/pause sequence
    // can spawn a parallel loop while the old one is stuck in await —
    // that was the "audio doesn't stop after play→pause→play→pause"
    // symptom hit during testing 2026-06-26.
    const r = this.resolveActive;
    if (r) {
      this.resolveActive = null;
      r();
    }
  }
}

/** Auto provider: tries server first; on any failure (network, 500, etc)
 *  silently falls back to the browser provider so the user doesn't get
 *  silence if the server happens to be down. The server + browser instances
 *  are injected (singletons declared below) so prefetch/speakSegment share
 *  state — otherwise a prefetch on the exported singleton would be wasted
 *  because the auto provider's internal server would re-fetch from scratch. */
export class AutoTTSProvider implements TTSProvider {
  name = "auto";
  private active: TTSProvider;
  constructor(
    private server: ServerTTSProvider,
    private browser: BrowserSpeechSynthesisProvider,
  ) {
    this.active = server;
  }

  async speakSegment(segment: TextSegment, options: TTSOptions): Promise<void> {
    try {
      this.active = this.server;
      await this.server.speakSegment(segment, options);
    } catch (err) {
      console.warn("[pagevoice] server TTS failed, falling back to browser", err);
      // CRITICAL: ensure the server's audio is fully stopped before the
      // browser TTS starts — otherwise both speak the same text at once.
      // The 'two voices' bug was here.
      this.server.stop();
      this.active = this.browser;
      await this.browser.speakSegment(segment, options);
    }
  }
  prefetch(segment: TextSegment, options: TTSOptions): void {
    this.server.prefetch(segment, options);
  }
  pause(): void { this.active.pause(); }
  resume(): void { this.active.resume(); }
  stop(): void { this.server.stop(); this.browser.stop(); }
}

// Singleton providers — the ReaderView picks one based on TTSSettings.provider.
// AutoTTSProvider is constructed with the Piper server so all prefetch +
// speakSegment calls hit the same instance; otherwise prefetch state would
// be orphaned and the same audio would be fetched twice. The Kokoro
// provider is its own ServerTTSProvider pointed at the /tts-kokoro/
// endpoint; it doesn't participate in the auto-fallback chain because the
// two engines have disjoint voice catalogs and the user explicitly picks
// which one they want to hear.
export const serverProvider = new ServerTTSProvider();
export const kokoroProvider = new ServerTTSProvider({
  endpoint: endpointFor("kokoro"),
  voice: "af_bella",
});
export const browserProvider = new BrowserSpeechSynthesisProvider();
export const autoProvider = new AutoTTSProvider(serverProvider, browserProvider);

import type { TTSProviderName } from "@/types/tts";

export function providerByName(name: TTSProviderName): TTSProvider {
  if (name === "server") return serverProvider;
  if (name === "kokoro") return kokoroProvider;
  if (name === "browser") return browserProvider;
  return autoProvider;
}

/** Backward-compat default. ReaderView still imports `ttsProvider`. */
export const ttsProvider: TTSProvider = autoProvider;

/** Stop EVERY provider singleton — used as a kill-switch when the user
 *  hits Stop or switches engines. Belt-and-suspenders: even if the
 *  active-provider state in React drifted out of sync (stale callback
 *  closure, mid-switch race), this guarantees nothing is left speaking
 *  in the background. */
export function stopAllProviders(): void {
  try { serverProvider.stop(); } catch { /* defensive */ }
  try { kokoroProvider.stop(); } catch { /* defensive */ }
  try { browserProvider.stop(); } catch { /* defensive */ }
  // autoProvider's stop already chains to its internal pair, but call it
  // explicitly too so the .active flag resets cleanly.
  try { autoProvider.stop(); } catch { /* defensive */ }
}

// ─────────────────────────────────────────────────────────────────────────
// Kokoro health check — same shape as Piper's checkServerTTS so the
// SettingsPanel can probe it on render and gate the voice picker on a
// reachable backend. Cached promise so a slow Kokoro doesn't slow renders.
// ─────────────────────────────────────────────────────────────────────────
let kokoroHealthPromise: Promise<ServerHealth | null> | null = null;
export function checkKokoroTTS(): Promise<ServerHealth | null> {
  if (kokoroHealthPromise) return kokoroHealthPromise;
  const url = endpointFor("kokoro").replace(/\/tts$/, "/health");
  kokoroHealthPromise = fetch(url, { method: "GET" })
    .then((r) => (r.ok ? (r.json() as Promise<ServerHealth>) : null))
    .catch(() => null)
    // Same negative-caching fix as checkServerTTS above — a failed probe
    // must not pin the voice picker empty for the rest of the session.
    .then((h) => {
      if (!h) kokoroHealthPromise = null;
      return h;
    });
  return kokoroHealthPromise;
}
export function recheckKokoroTTS(): Promise<ServerHealth | null> {
  kokoroHealthPromise = null;
  return checkKokoroTTS();
}

// ─────────────────────────────────────────────────────────────────────────
// Kokoro voice roster — canonical list from the pv-lib server (which
// always exposes the full built-in catalog). Falls back to a hard-coded
// list if pv-lib is unreachable so the picker still works offline. The
// live Kokoro health check advertises whatever it advertises; this is
// the *display* list, decoupled from what the engine happens to return
// at any given moment.
// ─────────────────────────────────────────────────────────────────────────
const KOKORO_ROSTER_FALLBACK = [
  "af_bella", "af_sarah", "af_nicole", "af_sky",
  "am_adam", "am_michael",
  "bf_alice", "bf_emma", "bf_isabella",
  "bm_george", "bm_lewis",
];

function libEndpoint(path: string): string {
  return remoteUrl(path);
}

let kokoroRosterPromise: Promise<string[]> | null = null;
export function fetchKokoroRoster(): Promise<string[]> {
  if (kokoroRosterPromise) return kokoroRosterPromise;
  const url = libEndpoint("/lib/kokoro-voices");
  kokoroRosterPromise = fetch(url, { method: "GET" })
    .then((r) => (r.ok ? (r.json() as Promise<{ voices: string[] }>) : null))
    .then((j) => (j?.voices?.length ? j.voices : KOKORO_ROSTER_FALLBACK))
    .catch(() => KOKORO_ROSTER_FALLBACK);
  return kokoroRosterPromise;
}

// ─────────────────────────────────────────────────────────────────────────
// Voice preview — plays a short sample sentence through the target engine
// so the user can audition a voice before committing. Reuses the singleton
// provider so the preview stops any in-flight preview cleanly.
// ─────────────────────────────────────────────────────────────────────────
export async function previewVoice(
  engine: "kokoro" | "server",
  voiceId: string,
  sampleName?: string,
): Promise<void> {
  const provider = engine === "kokoro" ? kokoroProvider : serverProvider;
  provider.stop();
  const name = sampleName ?? "this voice";
  const text =
    `The quick brown fox jumps over the lazy dog. ` +
    `This is a sample of ${name}'s voice reading a book.`;
  const seg: TextSegment = {
    id: `preview_${engine}_${voiceId}_${Date.now()}`,
    bookId: "preview",
    spineIndex: 0,
    href: "preview",
    text,
  };
  await provider.speakSegment(seg, {
    rate: 1.0,
    pitch: 1.0,
    volume: 1.0,
    serverVoice: voiceId,
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Volume-fade helper — used by the sleep timer. Interpolates the volume
// of every active provider from `from` to `to` over `durationMs`, then
// runs `onDone`. If playback is paused / stopped mid-fade, cancel via
// the returned dispose function.
// ─────────────────────────────────────────────────────────────────────────
export function fadeVolume(
  from: number,
  to: number,
  durationMs: number,
  onTick: (v: number) => void,
  onDone: () => void,
): () => void {
  const start = performance.now();
  let cancelled = false;
  let raf = 0;
  const step = (now: number) => {
    if (cancelled) return;
    const t = Math.min(1, (now - start) / Math.max(1, durationMs));
    const v = from + (to - from) * t;
    onTick(v);
    if (t >= 1) {
      onDone();
      return;
    }
    raf = requestAnimationFrame(step);
  };
  raf = requestAnimationFrame(step);
  return () => {
    cancelled = true;
    cancelAnimationFrame(raf);
  };
}
